import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { AuthProvider, portalPath, useAuth } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { Toaster } from "@/components/ui/sonner";
import { PublicNavbar } from "@/components/layout/PublicNavbar";
import { PublicFooter } from "@/components/layout/PublicFooter";
import { PortalShell } from "@/components/layout/PortalShell";
import { WhatsAppChatButton } from "@/components/common/WhatsAppChatButton";

import Landing from "@/pages/public/Landing";
import JobsList from "@/pages/public/JobsList";
import JobDetail from "@/pages/public/JobDetail";
import Login from "@/pages/public/Login";
import ResumeBuilderPage from "@/pages/public/ResumeBuilderPage";
import AboutUs from "@/pages/public/AboutUs";
import ContactUs from "@/pages/public/ContactUs";
import TermsPage from "@/pages/public/TermsPage";
import PrivacyPage from "@/pages/public/PrivacyPage";
import CookiePolicyPage from "@/pages/public/CookiePolicyPage";
import DisclaimerPage from "@/pages/public/DisclaimerPage";
import MyApplications from "@/pages/member/MyApplications";
import MemberApplicationDetail from "@/pages/member/MemberApplicationDetail";
import MemberResumePage from "@/pages/member/MemberResumePage";
import RecruiterDashboard from "@/pages/recruiter/RecruiterDashboard";
import UnassignedPool from "@/pages/recruiter/UnassignedPool";
import RecruiterApplications from "@/pages/recruiter/RecruiterApplications";
import RecruiterScheduled from "@/pages/recruiter/RecruiterScheduled";
import RecruiterCallbacks from "@/pages/recruiter/RecruiterCallbacks";
import RecruiterJoined from "@/pages/recruiter/RecruiterJoined";
import RecruiterProfile from "@/pages/recruiter/RecruiterProfile";
import SelectionList from "@/pages/shared/SelectionList";
import ApplicationWorkspace from "@/pages/shared/ApplicationWorkspace";
import CandidatesList from "@/pages/shared/CandidatesList";
import CandidateProfile from "@/pages/shared/CandidateProfile";
import CompaniesList from "@/pages/shared/CompaniesList";
import CompanyProfile from "@/pages/shared/CompanyProfile";
import EmploymentList from "@/pages/shared/EmploymentList";
import EmploymentRecord from "@/pages/shared/EmploymentRecord";
import FollowUps from "@/pages/shared/FollowUps";
import Lineups from "@/pages/shared/Lineups";
import Interviews from "@/pages/shared/Interviews";
import ForwardLeads from "@/pages/shared/ForwardLeads";
import WorkQueue from "@/pages/shared/WorkQueue";
import Notifications from "@/pages/shared/Notifications";
import LineupBoard from "@/pages/shared/LineupBoard";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminMembers from "@/pages/admin/AdminMembers";
import AdminJobs from "@/pages/admin/AdminJobs";
import AdminApplications from "@/pages/admin/AdminApplications";
import AdminBilling from "@/pages/admin/AdminBilling";
import AdminInvoicePending from "@/pages/admin/AdminInvoicePending";
import AdminAllocations from "@/pages/admin/AdminAllocations";
import AdminRecruiters from "@/pages/admin/AdminRecruiters";
import AdminDisputes from "@/pages/admin/AdminDisputes";
import AdminUpload from "@/pages/admin/AdminUpload";
import AdminMasterData from "@/pages/admin/AdminMasterData";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminActivityLogs from "@/pages/admin/AdminActivityLogs";
import ProfileConflicts from "@/pages/admin/ProfileConflicts";
import AdminScheduled from "@/pages/admin/AdminScheduled";

const SourceTracker = () => {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const source = params.get("source");
    if (source) localStorage.setItem("ut_source", source);
  }, []);
  return null;
};

const PublicLayout = () => (
  <div className="min-h-screen flex flex-col bg-background">
    <PublicNavbar />
    <main className="flex-1">
      <Outlet />
    </main>
    <PublicFooter />
  </div>
);

const RequireRole = ({ roles }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">Loading...</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to={portalPath(user.role)} replace />;
  return <PortalShell />;
};

function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <BrowserRouter>
          <SourceTracker />
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Landing />} />
            <Route path="/jobs" element={<JobsList />} />
            <Route path="/jobs/:jobId" element={<JobDetail />} />
            <Route path="/resume-builder" element={<ResumeBuilderPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="/disclaimer" element={<DisclaimerPage />} />
          </Route>

          <Route path="/member" element={<RequireRole roles={["member"]} />}>
            <Route index element={<MyApplications />} />
            <Route path="applications/:appId" element={<MemberApplicationDetail />} />
            <Route path="resume" element={<MemberResumePage />} />
          </Route>

          <Route path="/recruiter" element={<RequireRole roles={["recruiter"]} />}>
            <Route index element={<RecruiterDashboard />} />
            <Route path="unassigned" element={<UnassignedPool />} />
            <Route path="applications" element={<RecruiterApplications />} />
            <Route path="applications/:appId" element={<ApplicationWorkspace />} />
            <Route path="scheduled" element={<RecruiterScheduled />} />
            <Route path="callbacks" element={<RecruiterCallbacks />} />
            <Route path="joined" element={<RecruiterJoined />} />
            <Route path="candidates" element={<CandidatesList />} />
            <Route path="candidates/:candidateId" element={<CandidateProfile />} />
            <Route path="companies" element={<CompaniesList />} />
            <Route path="companies/:companyId" element={<CompanyProfile />} />
            <Route path="employment" element={<EmploymentList />} />
            <Route path="employment/:empId" element={<EmploymentRecord />} />
            <Route path="followups" element={<FollowUps />} />
            <Route path="interviews" element={<Interviews />} />
            <Route path="forward-leads" element={<ForwardLeads />} />
            <Route path="work-queue" element={<WorkQueue view="recruiter" />} />
            <Route path="lineup-board" element={<LineupBoard />} />
            <Route path="selection" element={<SelectionList />} />
            <Route path="profile" element={<RecruiterProfile />} />
          </Route>

          <Route path="/admin" element={<RequireRole roles={["admin", "super_admin"]} />}>
            <Route index element={<AdminDashboard />} />
            <Route path="fresh-leads" element={<UnassignedPool />} />
            <Route path="members" element={<AdminMembers />} />
            <Route path="candidates" element={<CandidatesList />} />
            <Route path="candidates/:candidateId" element={<CandidateProfile />} />
            <Route path="companies" element={<CompaniesList />} />
            <Route path="companies/:companyId" element={<CompanyProfile />} />
            <Route path="jobs" element={<AdminJobs />} />
            <Route path="applications" element={<AdminApplications />} />
            <Route path="applications/:appId" element={<ApplicationWorkspace />} />
            <Route path="employment" element={<EmploymentList />} />
            <Route path="employment/:empId" element={<EmploymentRecord />} />
            <Route path="billing" element={<AdminBilling />} />
            <Route path="invoice-pending" element={<AdminInvoicePending />} />
            <Route path="allocations" element={<AdminAllocations />} />
            <Route path="followups" element={<FollowUps />} />
            <Route path="lineups" element={<Lineups />} />
            <Route path="interviews" element={<Interviews />} />
            <Route path="forward-leads" element={<ForwardLeads />} />
            <Route path="work-queue" element={<WorkQueue view="admin" />} />
            <Route path="lineup-board" element={<LineupBoard />} />
            <Route path="scheduled" element={<AdminScheduled />} />
            <Route path="selection" element={<SelectionList />} />
            <Route path="recruiters" element={<AdminRecruiters />} />
            <Route path="upload-data" element={<AdminUpload />} />
            <Route path="disputes" element={<AdminDisputes />} />
            <Route path="master-data" element={<AdminMasterData />} />
            <Route path="profile-conflicts" element={<ProfileConflicts />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="activity-logs" element={<AdminActivityLogs />} />
          </Route>

          {/* M13: Notification Center — shared route for Admin + Recruiter (future Back Office/Accounts reuse it via RBAC mapping only) */}
          <Route path="/notifications" element={<RequireRole roles={["admin", "super_admin", "recruiter"]} />}>
            <Route index element={<Notifications />} />
          </Route>

          {/* M12: Back Office & Accounts work queues — fully functional, Admin-only until those roles are activated (RBAC mapping change only, no rewrites) */}
          <Route path="/backoffice" element={<RequireRole roles={["admin", "super_admin"]} />}>
            <Route index element={<Navigate to="/backoffice/work-queue" replace />} />
            <Route path="work-queue" element={<WorkQueue view="backoffice" />} />
          </Route>
          <Route path="/accounts" element={<RequireRole roles={["admin", "super_admin"]} />}>
            <Route index element={<Navigate to="/accounts/work-queue" replace />} />
            <Route path="work-queue" element={<WorkQueue view="accounts" />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
          <WhatsAppChatButton />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </SettingsProvider>
    </AuthProvider>
  );
}

export default App;
